import { create } from 'zustand'
import type { HandleMeta } from '@/lib/canvas/types'

interface HandleUpdateParams {
  nodeId: string
  handlesToAdd: HandleMeta[]
  handlesToUpdate: Array<{ id: string; updates: Partial<HandleMeta> }>
  handlesToRemove: string[]
}

interface CreatePlaceholderParams {
  parentNodeId: string
  type: 'image' | 'text' | 'video'
  count: number
}

interface NodeSettingsSnapshot {
  modelId: string
  modelFamilySlug?: string
  settings: Record<string, unknown>
}

interface SettingsApi {
  getSettings: () => Record<string, unknown> | null
  loadSnapshot: (snapshot: NodeSettingsSnapshot) => void
}

interface CanvasBridgeState {
  // Page-registered callbacks (called by nodes)
  handleUpdateCallback: ((params: HandleUpdateParams) => void) | null
  createPlaceholderCallback: ((params: CreatePlaceholderParams) => string[]) | null
  updateNodeStateCallback: ((nodeId: string, stateUpdate: Record<string, unknown>) => void) | null
  swapNodeTypeCallback: ((nodeId: string, newComponentType: string) => void) | null
  saveNodesCallback: ((nodes: unknown[]) => void) | null
  addCanvasNodesCallback: ((nodes: unknown[]) => void) | null
  addCanvasEdgesCallback: ((edges: unknown[]) => void) | null

  // RF method bridges (set by canvas-internals, called by page hooks)
  updateNodeInternals: ((nodeId: string) => void) | null
  fitView: ((nodeIds: string[]) => void) | null

  // Settings panel API (set by panel, read by nodes and page)
  settingsApi: SettingsApi | null
}

interface CanvasBridgeActions {
  registerHandleUpdate: (fn: CanvasBridgeState['handleUpdateCallback']) => void
  registerCreatePlaceholder: (fn: CanvasBridgeState['createPlaceholderCallback']) => void
  registerUpdateNodeState: (fn: CanvasBridgeState['updateNodeStateCallback']) => void
  registerSwapNodeType: (fn: CanvasBridgeState['swapNodeTypeCallback']) => void
  registerSaveNodes: (fn: CanvasBridgeState['saveNodesCallback']) => void
  registerAddCanvasNodes: (fn: CanvasBridgeState['addCanvasNodesCallback']) => void
  registerAddCanvasEdges: (fn: CanvasBridgeState['addCanvasEdgesCallback']) => void
  registerUpdateNodeInternals: (fn: CanvasBridgeState['updateNodeInternals']) => void
  registerFitView: (fn: CanvasBridgeState['fitView']) => void
  registerSettingsApi: (api: SettingsApi | null) => void
  reset: () => void
}

const initialState: CanvasBridgeState = {
  handleUpdateCallback: null,
  createPlaceholderCallback: null,
  updateNodeStateCallback: null,
  swapNodeTypeCallback: null,
  saveNodesCallback: null,
  addCanvasNodesCallback: null,
  addCanvasEdgesCallback: null,
  updateNodeInternals: null,
  fitView: null,
  settingsApi: null,
}

export const useCanvasBridgeStore = create<CanvasBridgeState & CanvasBridgeActions>((set) => ({
  ...initialState,
  registerHandleUpdate: (fn) => set({ handleUpdateCallback: fn }),
  registerCreatePlaceholder: (fn) => set({ createPlaceholderCallback: fn }),
  registerUpdateNodeState: (fn) => set({ updateNodeStateCallback: fn }),
  registerSwapNodeType: (fn) => set({ swapNodeTypeCallback: fn }),
  registerSaveNodes: (fn) => set({ saveNodesCallback: fn }),
  registerAddCanvasNodes: (fn) => set({ addCanvasNodesCallback: fn }),
  registerAddCanvasEdges: (fn) => set({ addCanvasEdgesCallback: fn }),
  registerUpdateNodeInternals: (fn) => set({ updateNodeInternals: fn }),
  registerFitView: (fn) => set({ fitView: fn }),
  registerSettingsApi: (api) => set({ settingsApi: api }),
  reset: () => set(initialState),
}))
